import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ClipboardCheck, Plus, Trash2, Loader2, CheckCircle2, Circle, CircleDot, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const RATING_TONE = {
  Outstanding: "bg-accent text-white", Good: "bg-emerald-600 text-white",
  "Requires improvement": "bg-amber-500 text-white", "Requires improvement to be good": "bg-amber-500 text-white", Inadequate: "bg-destructive text-white",
};
const NEXT = { open: "in_progress", in_progress: "done", done: "open" };
const STATUS_ICON = { open: Circle, in_progress: CircleDot, done: CheckCircle2 };

export default function Inspections() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [actionFor, setActionFor] = useState(null);
  const [form, setForm] = useState({ regulator: "CQC", inspection_date: "", overall_rating: "", kq_ratings: {}, summary: "" });
  const [act, setAct] = useState({ key_question: "", action: "", owner: "", due_date: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/inspections").then((r) => { setData(r.data); setForm((f) => ({ ...f, regulator: r.data.regulator })); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try { await api.post("/inspections", form); toast.success("Inspection recorded"); setOpen(false); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  const saveAction = async () => {
    setSaving(true);
    try {
      await api.post(`/inspections/${actionFor.id}/actions`, { ...act, due_date: act.due_date || null });
      toast.success("Action added"); setActionFor(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  const cycle = async (a) => { await api.patch(`/actions/${a.id}`, { status: NEXT[a.status] }); load(); };
  const removeAction = async (a) => { await api.delete(`/actions/${a.id}`); load(); };
  const removeInspection = async (i) => { await api.delete(`/inspections/${i.id}`); toast.success("Inspection removed"); load(); };

  if (!data) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  const ratings = data.ratings[form.regulator]; const kqs = data.key_questions[form.regulator];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="inspections-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <span className="overline text-accent">Inspection debrief</span>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Outcomes & improvement plan</h1>
          <p className="text-muted-foreground mt-2">Record each real inspection, then turn the report's findings into tracked actions.
            {data.overdue_actions > 0 && <span className="text-destructive font-medium"> {data.overdue_actions} overdue.</span>}</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="inspection-record-button"><ClipboardCheck className="w-4 h-4 mr-1.5" /> Record inspection</Button>
      </div>

      {data.items.length ? data.items.map((i) => (
        <div key={i.id} className="bg-card border border-border rounded-2xl p-6 shadow-sm mb-5" data-testid={`inspection-card-${i.id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <Badge className={RATING_TONE[i.overall_rating] || ""} data-testid={`inspection-rating-${i.id}`}>{i.overall_rating}</Badge>
                <span className="text-sm text-muted-foreground">{i.regulator} · {new Date(i.inspection_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</span>
              </div>
              <div className="flex gap-1.5 flex-wrap mt-2">
                {Object.entries(i.kq_ratings || {}).map(([k, v]) => (
                  <span key={k} className="text-[11px] px-2 py-0.5 rounded-full border border-border"><span className="text-muted-foreground">{k}:</span> {v}</span>
                ))}
              </div>
              {i.summary && <p className="text-sm text-muted-foreground mt-3 whitespace-pre-wrap">{i.summary}</p>}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setAct({ key_question: data.key_questions[i.regulator][0], action: "", owner: "", due_date: "" }); setActionFor(i); }} data-testid={`inspection-add-action-${i.id}`}><Plus className="w-4 h-4 mr-1" /> Action</Button>
              <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => removeInspection(i)} data-testid={`inspection-delete-${i.id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
          {i.actions.length > 0 && (
            <ul className="mt-4 divide-y divide-border border-t border-border" data-testid={`inspection-actions-${i.id}`}>
              {i.actions.map((a) => {
                const Icon = STATUS_ICON[a.status];
                return (
                  <li key={a.id} className="py-2.5 flex items-start gap-3 text-sm" data-testid={`action-row-${a.id}`}>
                    <button onClick={() => cycle(a)} className={`mt-0.5 ${a.status === "done" ? "text-accent" : a.overdue ? "text-destructive" : "text-muted-foreground"}`} title="Click to change status" data-testid={`action-status-${a.id}`}>
                      <Icon className="w-4 h-4" />
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={a.status === "done" ? "line-through text-muted-foreground" : ""}>{a.action}</p>
                      <p className="text-xs text-muted-foreground flex gap-2 flex-wrap mt-0.5">
                        <span>{a.key_question}</span>{a.owner && <span>· {a.owner}</span>}
                        {a.due_date && <span className={a.overdue ? "text-destructive font-medium" : ""}>· due {new Date(a.due_date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}{a.overdue && <AlertTriangle className="inline w-3 h-3 ml-1" />}</span>}
                        <span className="capitalize">· {a.status.replace("_", " ")}</span>
                      </p>
                    </div>
                    <button onClick={() => removeAction(a)} className="text-muted-foreground hover:text-destructive" data-testid={`action-delete-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )) : (
        <div className="text-center py-16 text-muted-foreground border border-dashed border-border rounded-2xl" data-testid="inspections-empty">
          <ClipboardCheck className="w-8 h-8 mx-auto mb-2 opacity-50" /><p>No inspections recorded yet.</p>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="inspection-dialog">
          <DialogHeader><DialogTitle className="font-display">Record an inspection</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Regulator</Label>
                <Select value={form.regulator} onValueChange={(v) => setForm({ ...form, regulator: v, overall_rating: "", kq_ratings: {} })}>
                  <SelectTrigger className="mt-1.5" data-testid="inspection-regulator"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="CQC">CQC</SelectItem><SelectItem value="Ofsted">Ofsted</SelectItem><SelectItem value="Fostering">Ofsted · Fostering agency</SelectItem><SelectItem value="Aesthetics">CQC · Aesthetics clinic</SelectItem><SelectItem value="Healthcare">CQC · GP & hospitals</SelectItem></SelectContent>
                </Select></div>
              <div><Label>Inspection date</Label><Input type="date" value={form.inspection_date} onChange={(e) => setForm({ ...form, inspection_date: e.target.value })} className="mt-1.5" data-testid="inspection-date" /></div>
            </div>
            <div><Label>Overall rating</Label>
              <Select value={form.overall_rating} onValueChange={(v) => setForm({ ...form, overall_rating: v })}>
                <SelectTrigger className="mt-1.5" data-testid="inspection-overall"><SelectValue placeholder="Choose" /></SelectTrigger>
                <SelectContent>{ratings.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select></div>
            <div>
              <Label>Ratings by key question (optional)</Label>
              <div className="mt-1.5 space-y-2">
                {kqs.map((k) => (
                  <div key={k} className="flex items-center gap-2">
                    <span className="text-sm flex-1">{k}</span>
                    <Select value={form.kq_ratings[k] || ""} onValueChange={(v) => setForm({ ...form, kq_ratings: { ...form.kq_ratings, [k]: v } })}>
                      <SelectTrigger className="w-44 h-8 text-xs" data-testid={`inspection-kq-${k.replace(/\W+/g, "-").toLowerCase()}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{ratings.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
            <div><Label>Key findings</Label><Textarea rows={3} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="mt-1.5" data-testid="inspection-summary" placeholder="What did the report highlight?" /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.inspection_date || !form.overall_rating} data-testid="inspection-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!actionFor} onOpenChange={(o) => !o && setActionFor(null)}>
        <DialogContent className="max-w-md" data-testid="action-dialog">
          <DialogHeader><DialogTitle className="font-display">Add improvement action</DialogTitle></DialogHeader>
          {actionFor && (
            <div className="space-y-4">
              <div><Label>Key question</Label>
                <Select value={act.key_question} onValueChange={(v) => setAct({ ...act, key_question: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="action-kq"><SelectValue /></SelectTrigger>
                  <SelectContent>{data.key_questions[actionFor.regulator].map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Action</Label><Textarea rows={2} value={act.action} onChange={(e) => setAct({ ...act, action: e.target.value })} className="mt-1.5" data-testid="action-text" placeholder="What will you do?" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Owner</Label><Input value={act.owner} onChange={(e) => setAct({ ...act, owner: e.target.value })} className="mt-1.5" data-testid="action-owner" /></div>
                <div><Label>Due date</Label><Input type="date" value={act.due_date} onChange={(e) => setAct({ ...act, due_date: e.target.value })} className="mt-1.5" data-testid="action-due" /></div>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="ghost" onClick={() => setActionFor(null)}>Cancel</Button>
            <Button onClick={saveAction} disabled={saving || act.action.trim().length < 3} data-testid="action-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
